/*
  Out of stock is stored as siblings inside the menu override node:

    settings/{r_id}/menu/{itemId}/outOfStock        "OUT_OF_STOCK"  (the flag the ordering site reads)
    settings/{r_id}/menu/{itemId}/outOfStockUntil   epoch ms, absent when forever
    settings/{r_id}/menu/{itemId}/outOfStockSince   epoch ms
    settings/{r_id}/menu/{itemId}/outOfStockForever true, only via the confirmed "forever" action
    settings/{r_id}/menu/{itemId}/outOfStockName    item name at the time it was marked

  Everything lives in one node so the ordering site can honor outOfStockUntil from the
  same read it already does, and so the flag and its expiry are written atomically.

  Items marked before timers existed have outOfStock with no Until/Since/Forever. They are
  left alone on purpose: nothing expires them, and they show up as "no timer" in the UI.
*/

export const OUT_OF_STOCK = 'OUT_OF_STOCK'

export const DURATIONS = [
  { key: '2h', label: '2 hrs', ms: 2 * 60 * 60 * 1000 },
  { key: '4h', label: '4 hrs', ms: 4 * 60 * 60 * 1000 },
  { key: 'today', label: 'Rest of day', restOfDay: true },
]

export function endOfDay (now) {
  const d = new Date(now)
  d.setHours(23, 59, 59, 999)
  return d.getTime()
}

export function untilFor (duration, now) {
  return duration.restOfDay ? endOfDay(now) : now + duration.ms
}

// One item's out-of-stock state, read from its override node.
// A boolean true shows up in old data, so anything truthy counts.
export function readStock (override, now) {
  if (!override || !override.outOfStock) { return null }
  const until = typeof override.outOfStockUntil === 'number' ? override.outOfStockUntil : null
  return {
    name: override.outOfStockName || override?.name?.en || null,
    since: typeof override.outOfStockSince === 'number' ? override.outOfStockSince : null,
    until,
    forever: !!override.outOfStockForever,
    // no until and not explicitly forever means it predates timers
    legacy: !until && !override.outOfStockForever,
    expired: until !== null && until <= now,
  }
}

// Every out-of-stock item in a menu override object, soonest expiry first,
// with untimed ones last since those are the ones that need attention.
export function collectOutOfStock (overrides, now) {
  const items = []
  Object.entries(overrides || {}).forEach(([itemId, override]) => {
    const stock = readStock(override, now)
    if (stock) { items.push({ itemId, ...stock }) }
  })
  return items.sort((a, b) => {
    const rank = (i) => (i.until ? 0 : i.forever ? 2 : 1)
    if (rank(a) !== rank(b)) { return rank(a) - rank(b) }
    if (a.until && b.until) { return a.until - b.until }
    return (a.since || 0) - (b.since || 0)
  })
}

export function markOutOfStock (menuRef, itemId, { until, forever, name }) {
  return menuRef.child(itemId).update({
    outOfStock: OUT_OF_STOCK,
    outOfStockSince: Date.now(),
    outOfStockUntil: forever ? null : until,
    outOfStockForever: forever ? true : null,
    outOfStockName: name || null,
  })
}

export function markInStock (menuRef, itemId) {
  return menuRef.child(itemId).update({
    outOfStock: null,
    outOfStockSince: null,
    outOfStockUntil: null,
    outOfStockForever: null,
    outOfStockName: null,
  })
}

export function formatRemaining (until, now) {
  const ms = until - now
  if (ms <= 0) { return 'expiring' }
  const mins = Math.round(ms / 60000)
  if (mins < 60) { return `${mins}m` }
  const hrs = Math.floor(mins / 60)
  const rem = mins % 60
  return rem ? `${hrs}h ${rem}m` : `${hrs}h`
}

export function formatAge (since, now) {
  if (!since) { return 'unknown' }
  const mins = Math.round((now - since) / 60000)
  if (mins < 60) { return `${mins}m` }
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) { return `${hrs}h` }
  return `${Math.floor(hrs / 24)}d`
}

export function abbreviate (name, max = 18) {
  if (!name) { return null }
  const clean = String(name).trim()
  return clean.length > max ? clean.slice(0, max - 1) + '…' : clean
}

// What to show when the item was marked before names were stored.
export function displayName (item, max) {
  return abbreviate(item.name, max) || item.itemId
}
